import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge, Button, Caption1, Checkbox, Field, Input, MessageBar, MessageBarBody, Select, Spinner, Text, Textarea, makeStyles,
} from '@fluentui/react-components';
import {
  OPEN_ADHOC_STATES, adHocFileCategoryOptions, formatRecordedCalendarDate, uploadKinds, uploadMimeSchema,
  type AdHocRequestSummary, type UploadMime,
} from '@vigeo/shared';
import { tokens } from '@vigeo/ui';
import { createAdHocRequest, fetchAdHocRequests } from './api.js';

const useStyles = makeStyles({
  panel: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: 0 },
  list: { listStyleType: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  row: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: tokens.spacingHorizontalS, minWidth: 0, overflowWrap: 'anywhere',
    paddingBlock: tokens.spacingVerticalS, borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: 0 },
  types: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalM },
});

const stateLabels: Record<AdHocRequestSummary['state'], string> = {
  not_started: 'Not opened yet', sent: 'Waiting on the person', in_progress: 'Person started', submitted: 'Ready for review',
  approved: 'Approved', rejected: 'Sent back', cancelled: 'Cancelled',
};
const isOpen = (state: AdHocRequestSummary['state']) => (OPEN_ADHOC_STATES as readonly string[]).includes(state);

export interface RequestsPanelProps {
  readonly personId: string;
  readonly canEdit: boolean;
}

function RequestRow({ request }: { readonly request: AdHocRequestSummary }) {
  const styles = useStyles();
  return <li className={styles.row}>
    <Text weight="semibold">{request.title}</Text>
    <Badge appearance="outline" size="medium" shape="rounded">{stateLabels[request.state]}</Badge>
    {request.categoryLabel === null ? null : <Caption1>{request.categoryLabel}</Caption1>}
    {request.segregated ? <Badge appearance="outline" size="small">Separate file</Badge> : null}
    {request.dueDate === null ? null : <Caption1>Due {formatRecordedCalendarDate(request.dueDate)}</Caption1>}
  </li>;
}

function RequestForm({ personId, onDone }: { readonly personId: string; readonly onDone: () => void }) {
  const styles = useStyles();
  const client = useQueryClient();
  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [types, setTypes] = useState<readonly UploadMime[]>(['application/pdf']);
  const [category, setCategory] = useState('');
  const [dueDate, setDueDate] = useState('');
  // One id per drafted request, so a double click cannot ask the person twice.
  const [operationId] = useState(() => crypto.randomUUID());
  const option = adHocFileCategoryOptions.find((o) => o.category === category);
  const create = useMutation({
    mutationFn: () => {
      if (option === undefined) throw new Error('Choose where the file is kept.');
      return createAdHocRequest(personId, { operationId, title, instructions, acceptedTypes: [...types], fileCategory: option.category, dueDate: dueDate === '' ? null : dueDate });
    },
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ['adhoc-requests', personId] }),
        client.invalidateQueries({ queryKey: ['timeline', personId] }),
      ]);
      onDone();
    },
  });
  const toggle = (mime: UploadMime, on: boolean) => setTypes((current) => on ? [...current, mime] : current.filter((m) => m !== mime));

  return <div className={styles.form}>
    <Field label="What do you need?" required><Input value={title} maxLength={120} onChange={(_, d) => setTitle(d.value)} /></Field>
    <Field label="Instructions for the person" hint="Shown with the upload in their portal."><Textarea value={instructions} resize="vertical" onChange={(_, d) => setInstructions(d.value)} /></Field>
    <Field label="Accepted file types" required>
      <div className={styles.types}>
        {uploadMimeSchema.options.map((mime) => <Checkbox key={mime} label={uploadKinds[mime].label} checked={types.includes(mime)} onChange={(_, d) => toggle(mime, d.checked === true)} />)}
      </div>
    </Field>
    <Field label="Where the file is kept" required hint={option?.segregated === true ? 'Kept in the separate, restricted file.' : undefined}>
      <Select value={category} onChange={(_, d) => setCategory(d.value)}>
        <option value="">Choose…</option>
        {adHocFileCategoryOptions.map((o) => <option key={o.category} value={o.category}>{o.label}{o.segregated ? ' (separate file)' : ''}</option>)}
      </Select>
    </Field>
    <Field label="Due date (optional)"><Input type="date" value={dueDate} onChange={(_, d) => setDueDate(d.value)} /></Field>
    <Caption1>A one-off request does not count toward the checklist. If this is a compliance requirement, ask for it to be added to the requirement catalog instead.</Caption1>
    {create.isError ? <MessageBar intent="error"><MessageBarBody>{create.error.message}</MessageBarBody></MessageBar> : null}
    <Button appearance="primary" disabled={create.isPending || title.trim() === '' || types.length === 0 || option === undefined} onClick={() => create.mutate()}>Create request</Button>
  </div>;
}

/**
 * One-off document requests (#62), listed BESIDE the checklist and never merged into it: they
 * satisfy no requirement, so they cannot change progress or clear a blocker. The person sees a
 * new request in their portal through the link HR sends from the Portal step.
 */
export function RequestsPanel({ personId, canEdit }: RequestsPanelProps) {
  const styles = useStyles();
  const [drafting, setDrafting] = useState(false);
  const [created, setCreated] = useState(false);
  const requests = useQuery({ queryKey: ['adhoc-requests', personId], queryFn: () => fetchAdHocRequests(personId) });

  const open = requests.data?.requests.filter((r) => isOpen(r.state)) ?? [];
  const closed = requests.data?.requests.filter((r) => !isOpen(r.state)) ?? [];
  return <div className={styles.panel}>
    {requests.isPending ? <Spinner size="small" label="Loading requests" /> : null}
    {requests.isError ? <MessageBar intent="error"><MessageBarBody>{requests.error.message}</MessageBarBody></MessageBar> : null}
    {requests.data ? <>
      <Text weight="semibold">Outstanding requests ({open.length})</Text>
      {open.length === 0 ? <Caption1>Nothing outstanding.</Caption1> : <ul className={styles.list} aria-label="Outstanding requests">{open.map((r) => <RequestRow key={r.instanceId} request={r} />)}</ul>}
      {closed.length === 0 ? null : <details><summary>Completed and cancelled ({closed.length})</summary><ul className={styles.list}>{closed.map((r) => <RequestRow key={r.instanceId} request={r} />)}</ul></details>}
    </> : null}
    {created ? <MessageBar intent="success"><MessageBarBody>Request created. It appears in the person’s portal; send them their portal link from the Portal step. The request email is held until secure-link sending is available.</MessageBarBody></MessageBar> : null}
    {canEdit ? (drafting
      ? <RequestForm key="form" personId={personId} onDone={() => { setDrafting(false); setCreated(true); }} />
      : <Button onClick={() => { setDrafting(true); setCreated(false); }}>Request a document</Button>) : null}
  </div>;
}
